import React, { useState, useEffect, useRef } from "react";
import * as Yup from "yup";
import { Formik, Field } from "formik";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../Style/CreateTask.css";
import "../Style/NewCustomer.css";
import MainSidebar from "./Mainsidebar";
import UserNavbar from "./UserNavbar";
import UserFooter from "./UserFooter";
import { path_url } from "../Config/config";
import { useNavigate } from "react-router-dom";
import countryData from "../Utils/countryData.json";

const TIMEZONE_OPTIONS = [
  "UTC_MINUS_12",
  "UTC_MINUS_11",
  "UTC_MINUS_10",
  "UTC_MINUS_9",
  "UTC_MINUS_8",
  "UTC_MINUS_7",
  "UTC_MINUS_6",
  "UTC_MINUS_5",
  "UTC_MINUS_4",
  "UTC_MINUS_3_30",
  "UTC_MINUS_3",
  "UTC_MINUS_2",
  "UTC_MINUS_1",
  "UTC",
  "UTC_PLUS_1",
  "UTC_PLUS_2",
  "UTC_PLUS_3",
  "UTC_PLUS_3_30",
  "UTC_PLUS_4",
  "UTC_PLUS_4_30",
  "UTC_PLUS_5",
  "UTC_PLUS_5_30",
  "UTC_PLUS_5_45",
  "UTC_PLUS_6",
  "UTC_PLUS_6_30",
  "UTC_PLUS_7",
  "UTC_PLUS_8",
  "UTC_PLUS_9",
  "UTC_PLUS_9_30",
  "UTC_PLUS_10",
  "UTC_PLUS_11",
  "UTC_PLUS_12",
  "UTC_PLUS_13",
  "UTC_PLUS_14",
  "PST",
  "MST",
  "CST",
  "EST",
  "GMT",
  "CET",
  "EET",
  "JST",
  "AEDT",
  "AEST",
  "ACST",
  "NZDT",
  "NZST",
];

const profileSchema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
});

// ── Label helper ─────────────────────────────────────────────────────────────
function FormLabel({ text, required }) {
  return (
    <label className="m-0 p-0 text-right" style={{ fontSize: 15 }}>
      <strong>{text}</strong>
      {required && (
        <sup
          style={{
            color: "red",
            top: 0,
            fontSize: 15,
            lineHeight: 0,
            position: "relative",
            verticalAlign: "baseline",
          }}
        >
          *
        </sup>
      )}
    </label>
  );
}

// ── Avatar / Photo picker ─────────────────────────────────────────────────────
function ProfileAvatar({ photoSrc, name, onPhotoChange }) {
  const [hovered, setHovered] = useState(false);
  const fileInputRef = useRef(null);

  const initials = (name || "U")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <div style={avatarStyles.wrapper}>
      {/* Clickable avatar circle */}
      <div
        style={{ ...avatarStyles.circle, cursor: "pointer" }}
        onClick={() => fileInputRef.current.click()}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        title="Click to change profile photo"
      >
        {/* Photo or initials */}
        {photoSrc ? (
          <img src={photoSrc} alt="Profile" style={avatarStyles.img} />
        ) : (
          <span style={avatarStyles.initials}>{initials}</span>
        )}

        {/* Dark overlay on hover */}
        <div style={{ ...avatarStyles.overlay, opacity: hovered ? 1 : 0 }}>
          {/* Camera SVG icon */}
          <svg width="30" height="30" viewBox="0 0 24 24" fill="white">
            <path d="M12 15.2A3.2 3.2 0 1 0 12 8.8a3.2 3.2 0 0 0 0 6.4z" />
            <path d="M9 3L7.17 5H4C2.9 5 2 5.9 2 7v13c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2h-3.17L15 3H9zm3 15c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z" />
          </svg>
          <span style={avatarStyles.overlayText}>Change Photo</span>
        </div>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          if (file.size > 5 * 1024 * 1024) {
            toast.error("Image must be smaller than 5 MB.");
            return;
          }
          const reader = new FileReader();
          reader.onload = (ev) => {
            const base64 = ev.target.result;
            onPhotoChange(base64, file);
          };
          reader.readAsDataURL(file);
        }}
      />

      <p style={avatarStyles.hint}>Click on photo to change</p>
    </div>
  );
}

const avatarStyles = {
  wrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: "1.5rem",
  },
  circle: {
    width: 100,
    height: 100,
    borderRadius: "50%",
    background: "linear-gradient(135deg, #4f46e5, #7c3aed)",
    border: "3px solid #e0e7ff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    position: "relative",
    boxShadow: "0 4px 14px rgba(79,70,229,0.25)",
    transition: "box-shadow 0.2s",
  },
  img: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    borderRadius: "50%",
  },
  initials: {
    color: "#fff",
    fontSize: "2rem",
    fontWeight: 700,
    letterSpacing: "0.04em",
    userSelect: "none",
  },
  overlay: {
    position: "absolute",
    inset: 0,
    background: "rgba(0,0,0,0.52)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    transition: "opacity 0.2s",
    borderRadius: "50%",
  },
  overlayText: {
    color: "#fff",
    fontSize: "0.65rem",
    fontWeight: 600,
    marginTop: 3,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
  },
  hint: {
    color: "#9ca3af",
    fontSize: "0.75rem",
    marginTop: 6,
    marginBottom: 0,
  },
};

// ── Main Component ────────────────────────────────────────────────────────────
export default function UserProfile() {
  const navigate = useNavigate();
  const [hamburger, sethamburger] = useState(false);
  const [isRightMenu, setisRightMenu] = useState(false);
  const [isSearchShow, setIsSearchShow] = useState(false);
  const [fullScreen, setFullScreen] = useState(true);
  const [dbValues, setDbValues] = useState(null);

  // Profile photo state
  const [photoSrc, setPhotoSrc] = useState(
    localStorage.getItem("userProfilePhoto") || "",
  );
  const [photoFile, setPhotoFile] = useState(null); // raw File for upload

  // ── Load profile data ─────────────────────────────────────────────────────
  useEffect(() => {
    const fetchProfile = async () => {
      const ls = (k) => localStorage.getItem(k) || "";
      const token = ls("accessToken");
      const adminId = ls("adminId");

      const fromStorage = {
        name: ls("name") || ls("userName"),
        email: ls("userEmail") || ls("email") || ls("companySettingVat"),
        contact:
          ls("userContact") || ls("contact") || ls("companySettingPhone"),
        designation: ls("mySettingRole"),
        skill: ls("userSkill"),
        country: ls("mySettingRegion"),
        state: ls("userState"),
        notes: ls("userNotes"),
        timezone: ls("mySettingTimeZone"),
      };
      setDbValues(fromStorage);

      if (!token || !adminId) return;
      const auth = { Authorization: `Bearer ${token}` };

      // Enrich from /user
      try {
        const { data } = await axios.get(`${path_url}/user`, { headers: auth });
        if (data) {
          const merged = {
            name: data.name || data.userName || fromStorage.name,
            email: data.email || data.emailId || fromStorage.email,
            contact:
              data.contact ||
              data.phoneNumber ||
              data.mobile ||
              fromStorage.contact,
            designation:
              data.designation || data.role || fromStorage.designation,
            skill: data.skill || data.skills || fromStorage.skill,
            country: data.country || fromStorage.country,
            state: data.state || fromStorage.state,
            notes: data.notes || fromStorage.notes,
            timezone: data.timezone || fromStorage.timezone,
          };
          setDbValues(merged);
          if (merged.email) localStorage.setItem("userEmail", merged.email);
          if (merged.contact)
            localStorage.setItem("userContact", merged.contact);
          if (merged.name) localStorage.setItem("name", merged.name);
          // Profile photo from backend (if URL is returned)
          if (data.profilePhoto || data.photo || data.imageUrl) {
            const url = data.profilePhoto || data.photo || data.imageUrl;
            setPhotoSrc(url);
            localStorage.setItem("userProfilePhoto", url);
          }
        }
      } catch (e) {
        console.warn("GET /user failed:", e?.response?.status);
      }

      // Fallback: /company/{adminId} for email/phone
      try {
        const { data } = await axios.get(`${path_url}/company/${adminId}`, {
          headers: auth,
        });
        const org = data?.body || data || {};
        setDbValues((prev) => ({
          ...prev,
          email: prev?.email || org.email || "",
          contact: prev?.contact || org.phoneNumber || "",
          country: prev?.country || org.country || "",
          state: prev?.state || org.state || "",
        }));
        if (org.email) localStorage.setItem("userEmail", org.email);
        if (org.phoneNumber)
          localStorage.setItem("userContact", org.phoneNumber);
      } catch (e) {
        console.warn("GET /company failed:", e?.response?.status);
      }
    };

    fetchProfile();
  }, []);

  // ── Photo change handler ──────────────────────────────────────────────────
  const handlePhotoChange = (base64, file) => {
    setPhotoSrc(base64);
    setPhotoFile(file);
    localStorage.setItem("userProfilePhoto", base64);
    toast.success("Photo selected — click Save to upload.");
  };

  // ── Upload photo to backend ───────────────────────────────────────────────
  const uploadPhotoToBackend = async () => {
    if (!photoFile) return;
    try {
      const token = localStorage.getItem("accessToken");
      const adminId = localStorage.getItem("adminId");
      if (!token || !adminId) return;

      const formData = new FormData();
      formData.append("file", photoFile);
      formData.append("adminId", adminId);

      await axios.post(`${path_url}/user/profile-photo`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
    } catch (e) {
      // Non-blocking — photo already saved in localStorage
      console.warn("Photo upload to backend failed:", e?.response?.status);
    }
  };

  // ── Layout helpers ────────────────────────────────────────────────────────
  const handleScreenEnter = () => {
    setFullScreen(false);
    document.body.requestFullscreen();
  };
  const handleScreenExit = () => {
    setFullScreen(true);
    document.exitFullscreen();
  };
  const handelShow = () => setIsSearchShow(!isSearchShow);
  const handelRightMenuShow = () => setisRightMenu(!isRightMenu);
  const handleclickonbutton = (hb) => {
    if (document.documentElement.clientWidth <= 800)
      return { width: "100%", left: "0rem" };
    return { width: !hb && "-webkit-fill-available", left: !hb && "3.7rem" };
  };
  const handleclickonbutton1 = (hb) => {
    if (document.documentElement.clientWidth <= 800)
      return { display: hb && "none" };
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="con1 d-flex">
      <div className="subcon1" style={handleclickonbutton1(hamburger)}>
        <MainSidebar hamburger={hamburger} sethamburger={sethamburger} />
      </div>

      <div className="subcon2" style={handleclickonbutton(hamburger)}>
        <UserNavbar
          hamburger={hamburger}
          sethamburger={sethamburger}
          handelRightMenuShow={handelRightMenuShow}
          handelShow={handelShow}
          isSearchShow={isSearchShow}
          handleScreenEnter={handleScreenEnter}
          handleScreenExit={handleScreenExit}
          fullScreen={fullScreen}
        />

        <div className="mt-2 min-vh-100">
          <Formik
            initialValues={
              dbValues || {
                name:
                  localStorage.getItem("name") ||
                  localStorage.getItem("userName") ||
                  "",
                email:
                  localStorage.getItem("userEmail") ||
                  localStorage.getItem("companySettingVat") ||
                  "",
                contact:
                  localStorage.getItem("userContact") ||
                  localStorage.getItem("companySettingPhone") ||
                  "",
                designation: localStorage.getItem("mySettingRole") || "",
                skill: localStorage.getItem("userSkill") || "",
                country: localStorage.getItem("mySettingRegion") || "",
                state: localStorage.getItem("userState") || "",
                notes: localStorage.getItem("userNotes") || "",
                timezone: localStorage.getItem("mySettingTimeZone") || "",
              }
            }
            enableReinitialize={true}
            validationSchema={profileSchema}
            onSubmit={async (values) => {
              try {
                const token = localStorage.getItem("accessToken");
                const adminId = localStorage.getItem("adminId");
                const userId = localStorage.getItem("userId");

                if (!token || !adminId) {
                  toast.error("Authentication missing. Please log in again.");
                  return;
                }

                const payload = {
                  id: userId,
                  adminid: adminId,
                  name: values.name,
                  designation: values.designation,
                  companyName: localStorage.getItem("mySettingCompany") || "",
                  country: values.country,
                  timezone: values.timezone || null,
                  skill: values.skill,
                  state: values.state,
                  notes: values.notes,
                  email: values.email,
                  contact: values.contact,
                };

                const response = await axios.put(`${path_url}/users`, payload, {
                  headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                  },
                });

                const ok =
                  response?.status === 200 ||
                  response?.data?.statusCodeValue === 200;
                // Also try uploading photo (non-blocking)
                await uploadPhotoToBackend();

                if (ok) {
                  localStorage.setItem("name", values.name);
                  localStorage.setItem("userEmail", values.email);
                  localStorage.setItem("userContact", values.contact);
                  localStorage.setItem("mySettingRole", values.designation);
                  localStorage.setItem("userSkill", values.skill);
                  localStorage.setItem("mySettingRegion", values.country);
                  localStorage.setItem("userState", values.state);
                  localStorage.setItem("userNotes", values.notes);
                  localStorage.setItem("mySettingTimeZone", values.timezone);
                  setPhotoFile(null); // photo already uploaded
                  toast.success("Profile saved successfully!");
                } else {
                  toast.error(
                    response?.data?.body || "Failed to save profile.",
                  );
                }
              } catch (error) {
                localStorage.setItem("name", values.name);
                localStorage.setItem("userEmail", values.email);
                localStorage.setItem("userContact", values.contact);
                localStorage.setItem("mySettingRole", values.designation);
                localStorage.setItem("userSkill", values.skill);
                localStorage.setItem("mySettingRegion", values.country);
                localStorage.setItem("userState", values.state);
                localStorage.setItem("userNotes", values.notes);
                localStorage.setItem("mySettingTimeZone", values.timezone);
                await uploadPhotoToBackend();
                setPhotoFile(null);
                toast.warning(
                  "Saved locally. Backend error: " +
                    (error?.response?.data?.message ||
                      error?.response?.data ||
                      error.message),
                );
              }
            }}
          >
            {(formik) => (
              <form onSubmit={formik.handleSubmit}>
                <fieldset>
                  <div className="col-12 d-flex justify-content-center">
                    <div className="bg-white col-11 cont">
                      {/* ── Header ── */}
                      <div className="d-flex mb-3">
                        <div
                          style={{ color: "gray" }}
                          className="col-12 m-1 mt-2 rounded shadow-sm elevation-2 pt-1"
                        >
                          <p className="text-white p-1 m-0">
                            <strong>User Profile</strong>
                          </p>
                        </div>
                      </div>

                      {/* ── Profile Photo ── */}
                      <ProfileAvatar
                        photoSrc={photoSrc}
                        name={formik.values.name}
                        onPhotoChange={handlePhotoChange}
                      />

                      {/* ── Form columns ── */}
                      <div className="d-flex flex-wrap">
                        {/* Left column */}
                        <div className="col-12 col-md-6 pl-0 pr-0">
                          {/* Name */}
                          <div className="col-12 col-sm-12 d-flex mb-2">
                            <div className="col-12 col-sm-4 justify-content-end text-right-lg">
                              <FormLabel text="Name" required />
                            </div>
                            <div className="col-12 col-sm-8 forms">
                              <input
                                type="text"
                                placeholder="Full Name"
                                name="name"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.name}
                              />
                              {formik.touched.name && formik.errors.name && (
                                <small style={{ color: "red" }}>
                                  {formik.errors.name}
                                </small>
                              )}
                            </div>
                          </div>

                          {/* Email */}
                          <div className="col-12 col-sm-12 d-flex mb-2">
                            <div className="col-12 col-sm-4 justify-content-end text-right-lg">
                              <FormLabel text="Email" />
                            </div>
                            <div className="col-12 col-sm-8 forms">
                              <input
                                type="email"
                                placeholder="Email"
                                name="email"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.email}
                              />
                            </div>
                          </div>

                          {/* Contact */}
                          <div className="col-12 col-sm-12 d-flex mb-2">
                            <div className="col-12 col-sm-4 justify-content-end text-right-lg">
                              <FormLabel text="Contact" />
                            </div>
                            <div className="col-12 col-sm-8 forms">
                              <input
                                type="text"
                                placeholder="Phone / Mobile"
                                name="contact"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.contact}
                              />
                            </div>
                          </div>

                          {/* Designation */}
                          <div className="col-12 col-sm-12 d-flex mb-2">
                            <div className="col-12 col-sm-4 justify-content-end text-right-lg">
                              <FormLabel text="Designation" />
                            </div>
                            <div className="col-12 col-sm-8 forms">
                              <input
                                type="text"
                                placeholder="Designation / Role"
                                name="designation"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.designation}
                              />
                            </div>
                          </div>

                          {/* Skills */}
                          <div className="col-12 col-sm-12 d-flex mb-2">
                            <div className="col-12 col-sm-4 justify-content-end text-right-lg">
                              <FormLabel text="Skills" />
                            </div>
                            <div className="col-12 col-sm-8 forms">
                              <input
                                type="text"
                                placeholder="Skills"
                                name="skill"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.skill}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Right column */}
                        <div className="col-12 col-md-6 pl-0 pr-0">
                          {/* Country */}
                          <div className="col-12 col-sm-12 d-flex mb-2">
                            <div className="col-12 col-sm-4 justify-content-end text-right-lg">
                              <FormLabel text="Country" />
                            </div>
                            <div className="col-12 col-sm-8">
                              <Field
                                as="select"
                                name="country"
                                id="country"
                                className="selectBox custom-select"
                              >
                                <option value="">Select Country</option>
                                {countryData.countries.map((c, i) => (
                                  <option key={i} value={c.country_name}>
                                    {c.country_name}
                                  </option>
                                ))}
                              </Field>
                            </div>
                          </div>

                          {/* State */}
                          <div className="col-12 col-sm-12 d-flex mb-2">
                            <div className="col-12 col-sm-4 justify-content-end text-right-lg">
                              <FormLabel text="State" />
                            </div>
                            <div className="col-12 col-sm-8 forms">
                              <input
                                type="text"
                                placeholder="State / Province"
                                name="state"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.state}
                              />
                            </div>
                          </div>

                          {/* Time Zone */}
                          <div className="col-12 col-sm-12 d-flex mb-2">
                            <div className="col-12 col-sm-4 justify-content-end text-right-lg">
                              <FormLabel text="Time Zone" />
                            </div>
                            <div className="col-12 col-sm-8">
                              <Field
                                as="select"
                                name="timezone"
                                id="timezone"
                                className="selectBox custom-select"
                              >
                                <option value="">Select Time Zone</option>
                                {TIMEZONE_OPTIONS.map((tz) => (
                                  <option key={tz} value={tz}>
                                    {tz.replace(/_/g, " ")}
                                  </option>
                                ))}
                              </Field>
                            </div>
                          </div>

                          {/* Notes */}
                          <div className="col-12 col-sm-12 d-flex mb-2">
                            <div className="col-12 col-sm-4 justify-content-end text-right-lg">
                              <FormLabel text="Notes" />
                            </div>
                            <div className="col-12 col-sm-8 forms">
                              <textarea
                                name="notes"
                                rows={3}
                                placeholder="Notes"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.notes}
                              />
                            </div>
                          </div>

                          {/* Cancel / Save */}
                          <div className="col-12 col-sm-12 d-flex pt-4">
                            <div className="col-12 col-sm-6 d-flex justify-content-center p-2">
                              <button
                                type="button"
                                className="btn btn-sm btn-light border rounded border-gray"
                                onClick={() => {
                                  navigate(-1);
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                            <div className="col-12 col-sm-6 d-flex justify-content-center p-2">
                              <button
                                type="submit"
                                className="btn btn-sm border rounded border-gray btn-info"
                                style={{
                                  backgroundColor: "rgba(52, 58, 64, 255)",
                                }}
                              >
                                Save
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </fieldset>
              </form>
            )}
          </Formik>
        </div>

        <UserFooter />
        <ToastContainer position="top-right" autoClose={3000} />
      </div>
    </div>
  );
}
